import {
  UpdateRadiatorRequest,
  ValveDirection,
  useGetApiTaxonomyRadiatorsByRadiatorIdQuery,
  usePatchApiTaxonomyRadiatorsByRadiatorIdMutation,
} from '@lib/api';
import clsx from 'clsx';
import { useEffect } from 'react';
import { OptionsValveDirection } from '../../';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
interface IProps {
  radiatorId: string;
  setSelectedDirection?: (val: ValveDirection | null) => void;
  setLoadingData?: (val: boolean) => void;
  onSuccess: () => void;
  setRadiatorName?: (name: string) => void;
  formId: string;
}

type ValveDirectionForm = Pick<UpdateRadiatorRequest, 'valveDirection'>;

const ThermostatPositionSchema: yup.ObjectSchema<ValveDirectionForm> = yup.object({
  valveDirection: yup.string<ValveDirection>().nullable().notOneOf([null]),
});

const defaultValues: ValveDirectionForm = { valveDirection: null };

export const ThermostatPositionComponent = (props: IProps) => {
  const { radiatorId, setLoadingData, setSelectedDirection, onSuccess, setRadiatorName, formId } = props;

  const { t } = useTranslation('wizard');
  const radiatorValveDirections = OptionsValveDirection(t, false);

  const { data: radiator, isLoading: isLoadingRadiator } = useGetApiTaxonomyRadiatorsByRadiatorIdQuery({ radiatorId });
  const [patchRadiator, { isLoading: isLoadingPatchRadiator }] = usePatchApiTaxonomyRadiatorsByRadiatorIdMutation();
  const isLoading = isLoadingRadiator || isLoadingPatchRadiator;

  const form = useForm({
    defaultValues,
    resolver: yupResolver(ThermostatPositionSchema),
  });
  const { handleSubmit, control, setValue } = form;

	const valveDirection = useWatch({ control, name: 'valveDirection' });

  useEffect(() => {
    if (radiator) {
      setRadiatorName?.(radiator.name);
    }
    if (radiator?.valveDirection) {
      setValue('valveDirection', radiator.valveDirection);
      setSelectedDirection?.(radiator.valveDirection);
    }
  }, [radiator, setRadiatorName, setSelectedDirection, setValue]);

  useEffect(() => {
    setLoadingData?.(isLoading);
  }, [setLoadingData, isLoading]);

  const selectDirection = (newDirection: ValveDirection) => {
    const newSelectedValveDirection = valveDirection === newDirection ? null : newDirection;
    setValue('valveDirection', newSelectedValveDirection);
    setSelectedDirection?.(newSelectedValveDirection);
  };

  const onSubmit: SubmitHandler<ValveDirectionForm> = ({ valveDirection }) => {
    if (valveDirection === null || !radiator) return;

    patchRadiator({
      radiatorId: radiatorId,
      body: {
        ...radiator,
        valveDirection,
      },
    })
      .unwrap()
      .then(onSuccess);
  };

  const imageClass = (direction: ValveDirection) =>
    clsx(valveDirection === direction && 'svg-invert', {
      'max-h-9': direction === 'Up' || direction === 'Down',
      '-rotate-90 max-w-9 ml-4': direction === 'Left',
      'rotate-90 max-w-9 mr-3': direction === 'Right',
      'rotate-180 max-h-9': direction === 'Down',
    });

  const imageSrc = (direction: ValveDirection) => {
    return direction === 'Front' ? '/ico-thermostat-front.svg' : '/ico-thermostat.svg';
  };

  const buttonClass = (direction: ValveDirection) =>
    clsx(valveDirection === direction ? 'bg-[#5AA7D0] text-white' : 'bg-[#F3F4F6]', 'rounded-lg h-20 flex items-center justify-center', {
      'flex-col': direction !== 'Left' && direction !== 'Right',
      'flex-row': direction === 'Left' || direction === 'Right',
      'col-start-2': direction === 'Up' || direction === 'Down',
      'col-start-1': direction === 'Left',
      'col-start-3': direction === 'Right',
    });

  return (
    <form onSubmit={handleSubmit(onSubmit)} id={formId}>
      <div className="grid grid-cols-3 gap-4 font-semibold text-sm">
        {radiatorValveDirections.map((item) => (
          <button
            type="button"
            onClick={() => selectDirection(item.value as ValveDirection)}
            className={buttonClass(item.value as ValveDirection)}
            key={`thermostat-position-${item.value}`}
          >
            {(item.value === 'Up' || item.value === 'Down' || item.value === 'Right') && (
              <>
                <img className={imageClass(item.value)} src={imageSrc(item.value)} alt="Thermostat" />
                {item.label}
              </>
            )}
            {(item.value === 'Left' || item.value === 'Front') && (
              <>
                {item.label}
                <img className={imageClass(item.value)} src={imageSrc(item.value)} alt="Thermostat" />
              </>
            )}
          </button>
        ))}
      </div>
    </form>
  );
};
